import React, { useEffect, useState, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';

import { useTranslate } from '../../../components/TranslationText';
import {
    eFormButtonsContainer,
    eformTerms,
} from '../../../common/cssclasses';
import { FormCheckbox } from '../../../components/FormInputs';
import { getCountries, getHorseColors } from '../../sharedAPI/lookUpApis';
import {
    getSemenById,
    getSemenOrderById,
    createSemenOrder,
    buildOrderFormData,
} from '../Api/semenMarketApi';
import SemenStallionInfoBar from '../Components/SemenStallionInfoBar';
import SemenMareDetails from '../Components/SemenMareDetails';
import SemenRacingResults from '../Components/SemenRacingResults';
import SemenCoveringLocation from '../Components/SemenCoveringLocation';
import SemenContractSection from '../Components/SemenContractSection';
import PaymentDocumentsForm from '../Components/PaymentDocumentsForm';
import { OrderStatus } from '../constants/orderStatus';
import { OrderActions } from '../constants/semenActions';
import SemenCheckoutHeader from '../Components/SemenCheckoutHeader';
import { buildSchema, defaultFormValues } from '../constants/checkoutSchema';
import SubmitButton from '../../../components/servicesButtons/SubmitButton';
import SemenReason from '../Components/SemenReason';
import SemenAdditionalDoseBanner from '../Components/SemenAdditionalDoseBanner';
import SemenBankInfo from '../Components/SemenBankInfo';
import SemenTypes from '../Components/SemenTypes';
import { ToastContainer, toast } from 'react-toastify';
import Loader from '../../../components/Loader';


// ─── Component ─────────────────────────────────────────────────
export default function SemenCheckoutPage() {
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const semenMarketId = searchParams.get('semenMarketId');
    const orderId = searchParams.get('orderId');
    const isVIPHorseParam = searchParams.get('isVip') === 'true';
    const isEditMode = !!orderId;

    // ─── React Hook Form ──────────────────────────────────────
    const methods = useForm({
        defaultValues: defaultFormValues,
        resolver: yupResolver(buildSchema(translate)),
        mode: 'onTouched',
        shouldFocusError: true, // Enabled by default
    });

    const {
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { isSubmitting },
    } = methods;

    const isRegisteredMare = watch('isRegisteredMare');

    // ─── Local State ──────────────────────────────────────────
    const [semenDetails, setSemenDetails] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [colors, setColors] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedMare, setSelectedMare] = useState(null);
    const [profile, setProfile] = useState(() => {
        const subAccountId = localStorage.getItem('currentSubAccountId');
        const fullName = localStorage.getItem('EAHSUserFullName');
        const userId = localStorage.getItem('EAHSUserID');
        if (subAccountId) {
            const userProfile = JSON.parse(localStorage.getItem('eahsUserProfile'));
            const match = userProfile?.subAccounts?.find(x => x.value === subAccountId);
            if (match) {
                const lang = localStorage.getItem('eahsLang') ?? 'en';
                return { label: match[`text${lang === 'en' ? '' : 'Ar'}`] || match.text || fullName, value: subAccountId };
            }
        }
        return { label: fullName, value: userId };
    });

    const actionParam = searchParams.get('action'); // See OrderActions
    const [isAdditionalDose, setIsAdditionalDose] = useState(actionParam === OrderActions.ExtraDose);

    // isBestHorse from API data, or fallback to URL param
    const isBestHorse = semenDetails?.isBestHorses ?? isVIPHorseParam;

    // Sync isBestHorse into the form so Yup's .test() can access it via this.parent
    useEffect(() => {
        setValue('isBestHorse', isBestHorse);
    }, [isBestHorse, setValue]);

    // Initialize isAdditionalDose from URL param
    useEffect(() => {
        if (actionParam === OrderActions.ExtraDose) {
            setIsAdditionalDose(true);
        }
    }, [actionParam]);

    const isFormDisabled = React.useMemo(() => {
        if (!orderId) return false; // New order flow
        if (isAdditionalDose) return false; // ExtraDose = new editable order
        if (actionParam === OrderActions.View) return true;
        if (actionParam === OrderActions.Payment) return true; // Payment action disables core fields
        if (actionParam === OrderActions.Edit) {
            const status = orderDetails?.status;
            // Only allow edit of core fields if status is Return
            if (status === OrderStatus.Return) {
                return false;
            }
            return true;
        }
        // Fallback if no action is provided: treat as default 'edit' permission check
        const status = orderDetails?.status;
        if (status === OrderStatus.Return) {
            return false;
        }
        return true;
    }, [actionParam, orderId, orderDetails, isAdditionalDose]);



    // ─── Lookup Data (skip in view mode — build from order details) ──
    useEffect(() => {
        if (actionParam === OrderActions.View) return;
        // ExtraDose is not 'view', so lookups ARE fetched
        getHorseColors().then(setColors);
        getCountries().then(setCountries);
    }, [actionParam]);

    // In view mode, populate dropdown options from order details
    useEffect(() => {
        if (actionParam !== OrderActions.View || !orderDetails) return;

        if (orderDetails.colorId) {
            setColors([{ value: orderDetails.colorId, text: orderDetails.colorName || orderDetails.color || '' }]);
        }
        if (orderDetails.originId) {
            setCountries((prev) => {
                const items = [...prev];
                if (!items.find((c) => c.value === orderDetails.originId)) {
                    items.push({ value: orderDetails.originId, text: orderDetails.originName || orderDetails.origin || '' });
                }
                if (orderDetails.coveringCountryId && !items.find((c) => c.value === orderDetails.coveringCountryId)) {
                    items.push({ value: orderDetails.coveringCountryId, text: orderDetails.coveringCountryName || orderDetails.coveringCountry || '' });
                }
                return items;
            });
        } else if (orderDetails.coveringCountryId) {
            setCountries([{ value: orderDetails.coveringCountryId, text: orderDetails.coveringCountryName || orderDetails.coveringCountry || '' }]);
        }
    }, [actionParam, orderDetails]);

    // ─── Fetch Semen Details ──────────────────────────────────
    useEffect(() => {
        if (semenMarketId) {
            getSemenById(semenMarketId).then(setSemenDetails);
        }
    }, [semenMarketId]);

    // ─── Fetch Order (edit mode) ──────────────────────────────
    useEffect(() => {
        if (orderId) {
            getSemenOrderById(orderId).then((order) => {
                if (!order) return;
                setOrderDetails(order);
                if (order.semenMarketId) {
                    getSemenById(order.semenMarketId).then(setSemenDetails);
                }
                setIsAdditionalDose(prev => prev || !!order.isAdditionalDose);
                const effectiveIsAdditionalDose = isAdditionalDose || order.isAdditionalDose;

                reset({
                    isRegisteredMare: !!order.isRegisteredMare,
                    isForExport: !!order.isForExport,
                    mareId: order.mareId || '',
                    mareNameEn: order.mareNameEn || order.mareName || '',
                    mareNameAr: order.mareNameAr || order.mareName || '',
                    sireNameEn: order.sireNameEn || order.sireName || '',
                    sireNameAr: order.sireNameAr || order.sireName || '',
                    damNameEn: order.damNameEn || order.damName || '',
                    damNameAr: order.damNameAr || order.damName || '',
                    mareBirthDate: order.mareBirthDate
                        ? order.mareBirthDate.split('T')[0]
                        : '',
                    colorId: order.colorId || null,
                    originId: order.originId || null,

                    mareRegisterNumber: order.mareRegisterNumber || '',
                    isMareOwner: !!order.isMareOwner,
                    ownerNameEn: order.mareOwnerNameEn || '',
                    ownerNameAr: order.mareOwnerNameAr || '',
                    coveringCountryId: order.coveringCountryId || null,
                    centerName: order.centerName || '',
                    centerNumber: order.centerNumber || '',
                    externalComment: effectiveIsAdditionalDose ? '' : (order.externalComment || ''),
                    orderStatus: effectiveIsAdditionalDose ? OrderStatus.Return : (order.status || ''),
                    acceptContract: effectiveIsAdditionalDose ? false : (order.status === OrderStatus.Paid || order.status === OrderStatus.Completed),
                    marePassport: order.marePassportFile?.fileUrl
                        ? [{ name: order.marePassportFile.name || 'passport', url: order.marePassportFile.fileUrl }]
                        : null,
                    paymentCertificate: !effectiveIsAdditionalDose && order.status !== OrderStatus.PendingPayment && order.paymentCertificateFile?.fileUrl
                        ? [{ name: order.paymentCertificateFile.name || 'certificate', url: order.paymentCertificateFile.fileUrl }]
                        : null,
                    signedContract: !effectiveIsAdditionalDose && order.status !== OrderStatus.PendingPayment && order.signedContractFile?.fileUrl
                        ? [{ name: order.signedContractFile.name || 'contract', url: order.signedContractFile.fileUrl }]
                        : null,
                    isBestHorse: !!order.isBestHorse,
                    semenType: order.semenType || 'frozen',
                    racingResults: order.racingResults?.length > 0
                        ? order.racingResults.map((rr) => ({
                            raceName: rr.raceName || '',
                            classification: rr.classification || '',
                            raceDate: rr.raceDate ? rr.raceDate.split('T')[0] : '',
                            rank: rr.rank ?? '',
                        }))
                        : [{ raceName: '', classification: '', raceDate: '', rank: '' }]
                });
                if (order.isRegisteredMare && order.mareId) {
                    setSelectedMare({
                        label: `${order.registerMare?.registerNumber} - ${order.registerMare?.name}`,
                        value: order.mareId,
                        details: order.registerMare || null,
                    });
                }

                // Set UserDropDown profile from order's owner data
                if (order.mareOwnerId) {
                    setProfile({
                        label: order.mareOwnerName || order.mareOwnerNameEn || '',
                        value: order.mareOwnerId,
                    });
                }

            });
        }
    }, [orderId, reset, isAdditionalDose]);
    console.log('isBestHorse ', isBestHorse);
    // ─── Submit Handler ───────────────────────────────────────
    const onSubmit = useCallback(
        async (data) => {
            try {
                const payload = {
                    ...data,
                    semenMarketId: semenMarketId || orderDetails?.semenMarketId,
                    ...(orderId && { orderId }),
                    ...(isAdditionalDose && { isAdditionalDose: true }),
                    ...(isRegisteredMare && selectedMare && { mareId: selectedMare.value }),
                    ownerId: profile?.value || localStorage.getItem('currentSubAccountId'),
                };

                const formData = buildOrderFormData(payload, data.racingResults);
                const res = await createSemenOrder(formData);
                toast.success(
                    translate(res?.responseText, 'Order submitted successfully')
                );
                // Delay one second so user sees the success toast, then dismiss before navigating
                // to avoid conflicting ToastContainer instances between pages
                await new Promise((resolve) => setTimeout(resolve, 1000));
                toast.dismiss();
                navigate('/profile?activeTab=semenRequests');
            } catch (err) {
                toast.error(
                    translate(err?.response?.data?.message, 'Something went wrong')
                );
            }
        },
        [semenMarketId, orderId, orderDetails?.semenMarketId, isRegisteredMare, selectedMare, isAdditionalDose, profile?.value, translate, navigate]
    );

    // ─── Validation error handler — scroll to racing results if needed ──
    const onFormError = useCallback((errors) => {
        if (errors?.racingResults) {
            const el = document.getElementById('racing-results-table');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, []);

    const dismissAll = () => toast.dismiss();
    // ─── Render ───────────────────────────────────────────────
    return (
        <>
            {/* toast */}
            <ToastContainer
                position="top-right"
                onClick={dismissAll}
                theme="colored"
                autoClose={5000}
            />
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit, onFormError)} className="row">
                    {/* ── Header ─────────────────────────────────── */}
                    <SemenCheckoutHeader
                        orderDetails={orderDetails}
                        semenDetails={semenDetails}
                        isAdditionalDose={isAdditionalDose}
                    />

                    {/* ── Stallion Info Bar ───────────────────────── */}
                    <SemenStallionInfoBar semenDetails={semenDetails} orderDetails={orderDetails} isAdditionalDose={isAdditionalDose} />

                    {/* ── Additional Dose Ref Banner ───────────────────────── */}
                    <SemenAdditionalDoseBanner orderDetails={orderDetails} semenMarketId={semenMarketId} />

                    {/* ── 1. Mare Details ─────────────────────────── */}
                    <SemenMareDetails
                        colors={colors}
                        countries={countries}
                        disabled={isFormDisabled}
                        selectedMare={selectedMare}
                        onSelectedMareChange={setSelectedMare}
                        profile={profile}
                    />

                    {/* ── 2. Racing Results ───────────────────────── */}
                    <SemenRacingResults
                        disabled={isFormDisabled}
                        isBestHorse={isBestHorse}
                    />

                    {/* ── 3. Semen Type ───────────────────────────── */}
                    <SemenTypes disabled={isFormDisabled} />

                    {/* ── 4. Covering Location (always shown, required only when exporting) */}
                    <SemenCoveringLocation
                        countries={countries}
                        disabled={isFormDisabled}
                    />


                    {/* ── Rejection/Return Reason ─────────────────── */}
                    {isEditMode && !isAdditionalDose && orderDetails?.externalComment && (
                        <SemenReason orderDetails={orderDetails} />
                    )}
                    {/* ── Contract ─────────────────────────────── */}
                    {isEditMode && !isAdditionalDose && (orderDetails?.status === OrderStatus.PendingPayment || orderDetails?.paymentCertificateFile?.fileUrl || orderDetails?.signedContractFile?.fileUrl) && (
                        <SemenContractSection
                            orderDetails={orderDetails}
                            disabled={actionParam === OrderActions.View}
                        />
                    )}

                    {/* ── Payment Documents (conditional) ─────────── */}
                    {isEditMode && !isAdditionalDose && (
                        orderDetails?.status === OrderStatus.PendingPayment
                            ? <PaymentDocumentsForm />
                            : (orderDetails?.paymentCertificateFile?.fileUrl || orderDetails?.signedContractFile?.fileUrl)
                                ? <PaymentDocumentsForm disabled={true} />
                                : null
                    )}

                    {/* ── Bank Info (read-only) ─────────────────── */}
                    {isEditMode && !isAdditionalDose && (
                        <SemenBankInfo orderDetails={orderDetails} />
                    )}

                    {/* ── Submit Button ──────────────────────────── */}
                    {actionParam !== OrderActions.View && (
                        <div className={`${eFormButtonsContainer} mt - 4 d - flex align - items - center flex - wrap mt-20`}>
                            {isEditMode && !isAdditionalDose && (orderDetails?.status === OrderStatus.PendingPayment || orderDetails?.paymentCertificateFile?.fileUrl || orderDetails?.signedContractFile?.fileUrl) && (
                                <div className={`${eformTerms} me - auto`}>
                                    <FormCheckbox
                                        name="acceptContract"
                                        label={translate('web.acceptContract', 'Accept Contract')}
                                        required
                                        noValidate={true}
                                        disabled={actionParam === OrderActions.View}
                                    />
                                </div>
                            )}
                            <SubmitButton
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    )}
                </form>
            </FormProvider>
        </>
    );
}

